import React, { useState } from 'react';
import { Mic, Volume2, Activity, Play, CheckCircle2, UserCheck } from 'lucide-react';
import { DEMO_STUDENTS } from '../../data/demoStudents';

export const SpeechTherapyModule: React.FC = () => {
  const [selectedStudentId, setSelectedStudentId] = useState(DEMO_STUDENTS[1].id); // Miss Ananya Kulkarni
  const [activeSound, setActiveSound] = useState('/r/');
  const [isRecording, setIsRecording] = useState(false);
  const [accuracyScore, setAccuracyScore] = useState(88);
  const [recordedMsg, setRecordedMsg] = useState('');

  const activeStudent = DEMO_STUDENTS.find(s => s.id === selectedStudentId) || DEMO_STUDENTS[1];

  const handleToggleRecording = () => {
    if (!isRecording) {
      setIsRecording(true);
      setRecordedMsg('Recording audio waveform for spectral analysis...');
    } else {
      setIsRecording(false);
      const newScore = Math.floor(80 + Math.random() * 18);
      setAccuracyScore(newScore);
      setRecordedMsg(`Analysis complete! Pronunciation accuracy for ${activeStudent.name}: ${newScore}%`);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-6">
      
      {/* Demo Student Selector */}
      <div className="bg-blue-50 border border-blue-200 p-3 rounded-xl flex flex-col md:flex-row items-center justify-between gap-3 text-xs">
        <div className="flex items-center gap-2">
          <UserCheck className="w-4 h-4 text-blue-600 shrink-0" />
          <span className="font-bold text-slate-800">Assigned Student:</span>
          <select 
            value={selectedStudentId}
            onChange={(e) => setSelectedStudentId(e.target.value)}
            className="px-2.5 py-1 bg-white border border-blue-300 rounded-lg font-bold text-blue-900 focus:ring-2 focus:ring-blue-500"
          >
            {DEMO_STUDENTS.map(st => (
              <option key={st.id} value={st.id}>{st.name} ({st.rollId}) - {st.disabilityCategory}</option>
            ))}
          </select>
        </div>

        <div className="text-[11px] text-blue-800 font-semibold truncate">
          Therapist: <span className="font-bold">{activeStudent.therapist}</span>
        </div>
      </div>

      <div className="flex items-center justify-between border-b border-slate-100 pb-4">
        <div>
          <span className="px-2.5 py-0.5 bg-cyan-100 text-cyan-800 text-[10px] font-bold rounded-full uppercase tracking-wider">
            Clinical Articulation Engine
          </span>
          <h2 className="text-2xl font-bold text-slate-900 mt-1">Speech Therapy & Articulation Drills</h2>
          <p className="text-xs text-slate-500">Phonetic sound frequency analysis and real-time audio waveform analyzer for {activeStudent.name}</p>
        </div>

        <button 
          onClick={handleToggleRecording}
          className={`px-4 py-2 text-white font-bold text-xs rounded-xl shadow transition flex items-center gap-2 ${
            isRecording ? 'bg-red-600 animate-pulse' : 'bg-cyan-600 hover:bg-cyan-700'
          }`}
        >
          <Mic className="w-4 h-4" />
          <span>{isRecording ? 'Stop Recording' : 'Record Patient Voice'}</span>
        </button>
      </div>

      {recordedMsg && (
        <div className="p-3 bg-cyan-50 border border-cyan-200 text-cyan-900 rounded-xl text-xs font-bold flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-cyan-600" />
          {recordedMsg}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
          <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Select Phonetic Sound Target</h3>
          <div className="flex gap-2 flex-wrap">
            {['/r/', '/s/', '/th/', '/l/', '/k/'].map(snd => (
              <button 
                key={snd}
                onClick={() => setActiveSound(snd)}
                className={`px-4 py-2 rounded-xl text-xs font-bold transition ${
                  activeSound === snd ? 'bg-cyan-600 text-white shadow' : 'bg-white text-slate-700 border'
                }`}
              >
                {snd}
              </button>
            ))}
          </div>
        </div>

        <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
          <div className="text-xs font-bold text-slate-500">Automated Pronunciation Accuracy Score</div>
          <div className="text-3xl font-black text-cyan-600 font-mono">{accuracyScore}% Match</div>
          <div className="w-full bg-slate-200 h-2.5 rounded-full overflow-hidden">
            <div className="bg-cyan-600 h-full transition-all duration-500" style={{ width: `${accuracyScore}%` }}></div>
          </div>
        </div>
      </div>
    </div>
  );
};
